/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiButton, EuiButtonEmpty, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import type { AnyProject } from '@modules/project-api/types'
import type { ProjectApiError, ProjectType, ProjectID } from '@modules/ui-types/projects'

import ReviewAndSaveStep from './ReviewAndSaveStep'

type Props = {
  projectId: ProjectID
  projectType: ProjectType
  selectedProjects: AnyProject[]
  projectName: string
  isEditing: boolean
  linkError?: ProjectApiError | null
  isSaving: boolean
  onCancel: () => void
  onPrevious: () => void
  onSave: () => void
}

const ReviewAndSaveStepContainer: React.FunctionComponent<Props> = ({
  projectId,
  projectType,
  selectedProjects,
  projectName,
  isEditing,
  linkError,
  isSaving,
  onCancel,
  onPrevious,
  onSave,
}) => (
  <EuiFlexGroup direction='column' gutterSize='m' alignItems='center'>
    <EuiFlexItem style={{ maxWidth: '60rem', width: '100%' }}>
      <ReviewAndSaveStep
        projectId={projectId}
        projectType={projectType}
        selectedProjects={selectedProjects}
        projectName={projectName}
        isEditing={isEditing}
      />
    </EuiFlexItem>

    {linkError && (
      <EuiFlexItem style={{ maxWidth: '60rem', width: '100%' }}>
        <ProjectApiErrorCallout error={linkError} />
      </EuiFlexItem>
    )}

    <EuiFlexItem style={{ maxWidth: '60rem', width: '100%' }}>
      <EuiFlexGroup justifyContent='flexEnd' gutterSize='m'>
        <EuiFlexItem grow={false}>
          <EuiButtonEmpty
            onClick={onCancel}
            disabled={isSaving}
            data-test-subj='cps-reviewAndSaveStep-cancelButton'
          >
            <FormattedMessage id='cps.link.cancel' defaultMessage='Cancel' />
          </EuiButtonEmpty>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={onPrevious}
            disabled={isSaving}
            data-test-subj='cps-reviewAndSaveStep-previousButton'
          >
            <FormattedMessage id='cps.link.previous' defaultMessage='Previous' />
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            fill={true}
            onClick={onSave}
            isLoading={isSaving}
            data-test-subj='cps-reviewAndSaveStep-saveButton'
          >
            <FormattedMessage id='cps.link.save' defaultMessage='Save' />
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiFlexItem>
  </EuiFlexGroup>
)

export default ReviewAndSaveStepContainer
